import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import {
  Avatar,
  Container,
  Divider,
  Fade,
  Link,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { SWAPSpace, Typography } from '@yosgo/swap-ui'
import Head from 'next/head'
import { ReactChild, useEffect, useState } from 'react'
import PhotoAlbum from 'react-photo-album'
import { Skeleton } from '@material-ui/lab'

import { FavIcon, WebsiteName } from '../Common/PagesHead'

interface ContentProps {
  type: 'h4' | 'h6' | 'body1_loose' | 'imageAndText' | 'gallery'
  content:
    | string
    | ReactChild
    | Array<{ src: string; width: number; height: number }>
  image?: { src: string; width: number; height: number }
  caption?: string | ReactChild
  imagePosition?: 'left' | 'right' | 'top' | 'bottom'
  layout?: 'rows' | 'columns' | 'masonry'
}

export interface InterviewProps {
  slug: string
  name: string
  cover: string
  featureImage?: string
  index: number
  post_created_at: string
  title: string | ReactChild
  avatar: string
  selfBrief: string
  links: Array<{
    title: string
    url: string
  }>
  contents: Array<ContentProps>
  nextPost: string
  nextPostUrl: string
}

const FeatureImageBlock = ({ src }: { src?: string }) => {
  const [index, setIndex] = useState(-1)

  return (
    <>
      {src ? (
        <>
          <img
            onClick={() => setIndex(0)}
            src={src}
            style={{
              width: '100%',
              height: 'auto',
              borderRadius: '1px',
              filter: 'drop-shadow(0px 6px 24px rgba(0, 0, 0, 0.1))',
            }}
          />
          {src ? (
            <Lightbox
              open={index >= 0}
              index={index}
              close={() => setIndex(-1)}
              slides={[
                {
                  src,
                },
              ]}
              carousel={{
                finite: true,
              }}
            />
          ) : null}
          <SWAPSpace size={40} />
        </>
      ) : null}
    </>
  )
}
const ImageWidthTextBlock = ({ content }: { content: ContentProps }) => {
  const theme = useTheme()
  const smDown = useMediaQuery(theme.breakpoints.down('sm'))

  const rowPosition =
    content?.imagePosition === 'left' || content?.imagePosition === 'right'
  const columnPosition =
    content?.imagePosition === 'top' || content?.imagePosition === 'bottom'

  const [index, setIndex] = useState(-1)

  return (
    <>
      {content.type === 'imageAndText' ? (
        <div
          className="text_image_block"
          style={{
            display: 'flex',
            gap: '24px',
            flexWrap: smDown ? 'wrap' : 'nowrap',
            alignItems: 'center',
            flexDirection:
              content.imagePosition === 'left'
                ? 'row'
                : content.imagePosition === 'right'
                ? 'row-reverse'
                : content.imagePosition === 'top'
                ? 'column'
                : content.imagePosition === 'bottom'
                ? 'column-reverse'
                : 'row',
          }}
        >
          {content?.image?.src ? (
            <div
              style={{
                flex: rowPosition ? '1 0 auto' : '1 1 100%',
                maxWidth: columnPosition ? '100%' : smDown ? '100%' : '25%',
                marginBottom: rowPosition ? (smDown ? 20 : 40) : 0,
              }}
            >
              <img
                onClick={() => setIndex(0)}
                src={content.image.src}
                style={{
                  borderRadius: '4px',
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto',
                  filter: 'drop-shadow(0px 6px 24px rgba(0, 0, 0, 0.1))',
                }}
              />
              <SWAPSpace size={12} />
              <Typography
                variant="small1"
                color="black800"
                style={{ textAlign: 'center' }}
              >
                {content.caption}
              </Typography>
            </div>
          ) : null}
          <div style={{ flex: '1 1 100%' }}>
            <Typography
              variant="body1_loose"
              color={'black900'}
              style={{ fontSize: '18.3px' }}
            >
              {content.content}
            </Typography>
          </div>
          {content?.image ? (
            <Lightbox
              open={index >= 0}
              index={index}
              close={() => setIndex(-1)}
              slides={[content.image]}
              carousel={{
                finite: true,
              }}
            />
          ) : null}
        </div>
      ) : null}
    </>
  )
}
const TextBlock = ({ content }: { content: ContentProps }) => {
  return (
    <>
      {content.type === 'body1_loose' ||
      content.type === 'h6' ||
      content.type === 'h4' ? (
        <div className="text_block">
          {content.type === 'h6' || content.type === 'h4' ? (
            <SWAPSpace size={36} />
          ) : null}
          <Typography
            variant={content.type}
            color={
              content.type === 'h4'
                ? 'primary800'
                : content.type === 'h6'
                ? 'primary800'
                : 'black900'
            }
            style={content.type === 'body1_loose' ? { fontSize: '18.3px' } : {}}
          >
            {content.content}
          </Typography>
          <SWAPSpace size={24} />
        </div>
      ) : null}
    </>
  )
}
const GalleryBlock = ({ content }: { content: ContentProps }) => {
  const [index, setIndex] = useState(-1)
  return (
    <>
      {content.type === 'gallery' ? (
        <div className="gallery_block">
          {Array.isArray(content.content) ? (
            <PhotoAlbum
              layout={content?.layout || 'rows'}
              photos={content.content}
              onClick={(_, __, index) => setIndex(index)}
            />
          ) : null}
          {content.caption ? (
            <>
              <SWAPSpace size={12} />
              <Typography
                variant="small1"
                color="black800"
                style={{ textAlign: 'center' }}
              >
                {content.caption}
              </Typography>
            </>
          ) : null}
          {Array.isArray(content.content) ? (
            <Lightbox
              open={index >= 0}
              index={index}
              close={() => setIndex(-1)}
              slides={content.content}
            />
          ) : null}
          <SWAPSpace size={40} />
        </div>
      ) : null}
    </>
  )
}
const Interview = ({
  slug,
  name,
  cover,
  index,
  post_created_at,
  title,
  avatar,
  featureImage,
  selfBrief,
  links,
  contents,
  nextPost,
  nextPostUrl,
}: InterviewProps) => {
  const [loading, setLoading] = useState(true)
  const HeadTitle = `${name}專訪 | ${WebsiteName}`

  const theme = useTheme()
  const mdDown = useMediaQuery(theme.breakpoints.down('md'))

  useEffect(() => {
    setTimeout(() => {
      setLoading(false)
    }, 100)
  }, [])

  return (
    <>
      <Head>
        <link rel="icon" href={FavIcon} />
        <link rel="canonical" href="https://swap.work/" />
        <title>{HeadTitle}</title>
        <meta property="og:title" content={HeadTitle} />
        <meta property="og:description" content={selfBrief} />
        <meta
          property="og:image"
          content={`${cover}?v=${new Date().getTime()}`}
        />
        <meta property="og:url" content={`${APP_URL}/interview/${slug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={HeadTitle} />
        <meta name="description" content={selfBrief} />
      </Head>
      {loading ? (
        <Fade in>
          <Container maxWidth="xl">
            <div>
              <SWAPSpace size={80} />
              <Typography variant="title" color="primary800">
                SWAP 自由工作者系列專訪 #{index}
              </Typography>
              <SWAPSpace />
              <Skeleton variant="text" height={'60px'} />
              <Skeleton variant="text" height={'60px'} width="80%" />
              <SWAPSpace size={40} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} width="80%" />
              <SWAPSpace size={40} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} width="80%" />
              <SWAPSpace size={80} />
              <SWAPSpace size={40} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} />
              <Skeleton variant="text" height={'36px'} width="80%" />
              <SWAPSpace size={80} />
            </div>
          </Container>
        </Fade>
      ) : (
        <Fade in timeout={1000}>
          <Container maxWidth="xl">
            {/* 頁首 */}
            <div>
              <SWAPSpace size={92} />
              <Typography variant="title" color="primary800">
                SWAP 自由工作者系列專訪 #{index}
              </Typography>
              <h1
                style={{
                  lineHeight: mdDown ? '42px' : '52px',
                  fontSize: mdDown ? '32px' : '42px',
                }}
              >
                {title}
              </h1>
              <SWAPSpace size={40} />
              <Divider />
              <SWAPSpace size={40} />
              <FeatureImageBlock src={featureImage} />
            </div>
            {/* 內容 */}
            <div>
              {contents.map((item, index) => (
                <div key={`${name}_p_${index}`}>
                  {/* 圖配內文區域 */}
                  <ImageWidthTextBlock content={item} />
                  {/* 文字段落區域 */}
                  <TextBlock content={item} />
                  {/* 圖片展覽區域 */}
                  <GalleryBlock content={item} />
                </div>
              ))}
            </div>
            {/* 頁尾 */}
            <div>
              <SWAPSpace size={40} />
              <Divider />
              <SWAPSpace size={40} />
              {/* 會員關於 */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: mdDown ? '24px' : '48px',
                  flexWrap: mdDown ? 'wrap' : 'nowrap',
                  justifyContent: mdDown ? 'center' : 'auto',
                }}
              >
                <div>
                  <style>
                    {`
                    img {
                        object-position: top;
                    }
                    `}
                  </style>
                  <Avatar
                    src={avatar}
                    style={{
                      width: '180px',
                      height: '180px',
                    }}
                  />
                </div>
                <div>
                  <Typography variant="h6">About</Typography>
                  <SWAPSpace size={12} />
                  <Typography variant="body1">{selfBrief}</Typography>
                  <SWAPSpace />
                  <div style={{ display: 'flex' }}>
                    {links.map((item, index) => (
                      <div key={`link_${index}`}>
                        <Link
                          href={item.url}
                          target="_blank"
                          underline="always"
                        >
                          {item.title}
                        </Link>
                        {links.length - 1 === index ? null : '・'}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <SWAPSpace size={40} />
              <Divider />
              <SWAPSpace size={40} />
              {/* 文章備註 */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: mdDown ? 'center' : 'space-between',
                  gap: '12px',
                  alignItems: 'flex-end',
                  flexWrap: mdDown ? 'wrap' : 'nowrap',
                }}
              >
                <div style={mdDown ? { flex: '1 1 100%' } : {}}>
                  <Typography variant="body2" color="black1000">
                    文章發布於 {post_created_at}
                  </Typography>
                  <SWAPSpace size={36} />
                  <Typography variant="body2" color="black1000">
                    採訪者:{' '}
                    <Link
                      underline="always"
                      target="blank"
                      href="https://swap.work/profile/cyc_content_studio"
                    >
                      陳芷儀
                    </Link>
                  </Typography>
                  <SWAPSpace size={3} />
                  <Typography variant="body2" color="black1000">
                    協力執行: 耳草人內容工作室、真識團隊
                  </Typography>
                </div>
                <div
                  style={mdDown ? { flex: '1 1 100%', marginTop: '24px' } : {}}
                >
                  <Link
                    href={nextPostUrl}
                    style={{ fontSize: '18px', fontWeight: 'bold' }}
                  >
                    <Typography variant="small1" color="primary800">
                      自由工作者系列專訪
                    </Typography>
                    <SWAPSpace size={6} />
                    {nextPost} →
                  </Link>
                </div>
              </div>
              <SWAPSpace size={80} />
            </div>
          </Container>
        </Fade>
      )}
    </>
  )
}

export default Interview
